/**
 * バック計算の唯一の入口 (v4.0.0)
 *
 * 背景: 同じ「この会計で誰にいくら」を、以下の5箇所が別々に計算していた。
 *   1. Checkout.saveIncentiveRecords     … staffIncentives へ書く額
 *   2. BackApproval.tsx (PC/店長)        … 承認画面に出る額（shareRate を % と誤読）
 *   3. back_approval_screen.dart (アプリ) … 承認前の内訳
 *   4. my_sales_screen.dart (アプリ/キャスト) … 本人が見る額（税抜化と利益控除を掛けていなかった）
 *   5. NightPro (Laravel)                … 別サーバ
 * 結果、キャストのアプリと店長の承認画面が同じ商品に別の金額を出していた。
 *
 * このファイルは 1〜4 の計算元を1本にする。入力は「会計1件ぶんの素材」、
 * 出力は「スタッフID別の売上・分配対象額・バック額」。
 * 表示・保存・承認はすべてこの結果を使う。
 */

import type {
  CategoryDistributionConfig,
  StaffDistributionConfig,
  OrderItemForDistribution,
} from './salesDistribution';
import { computeEffectiveExtensionPrice } from './extensionPricing';
import type { PlanOrderLike } from './extensionPricing';
import { summarizeAllowances } from './allowance';
import type { AppliedAllowance } from './allowance';

/** 卓に付いたキャスト1人ぶんの在席記録（sessions.helpStaff 相当） */
export interface AttendingCast {
  staffId: string;
  staffName: string;
  /** 'primary' = 本指名 / 'banai' = 場内 / 'help' = ヘルプ / 'back' = 付け回り */
  role: 'primary' | 'banai' | 'help' | 'back';
  assignedAt: Date;
  releasedAt?: Date | null;
}

export interface NominationInput {
  staffId?: string;
  staffName?: string;
  type?: 'honshimei' | 'banai' | 'none';
  fee: number;
}

export interface BackCalculationInput {
  items: OrderItemForDistribution[];
  planOrders?: PlanOrderLike[];
  /** 卓に付いたキャスト。指定が無い商品の分配先になる */
  attendingCasts?: AttendingCast[];
  /** 会計画面で明示的に選ばれた売上スタッフ（'__shop__' を含みうる） */
  selectedSalesStaff?: { staffId: string; staffName: string; shareRate: number }[];
  nominations?: NominationInput[];
  /**
   * 会計に付けられた手当（同伴・指名・ボトルバック等）。
   * 以前は NightPro 側にしか無く、Firestore の給与計算に入っていなかった。
   */
  allowances?: AppliedAllowance[];

  categoryMap: Map<string, CategoryDistributionConfig>;
  staffMap: Map<string, StaffDistributionConfig>;
  /** スタッフID → バック率（0-1）。未設定なら item.costInfo.baseBackRate → 店舗既定 */
  staffBackRates?: Map<string, number>;
  defaultBackRate: number;

  taxRate: number;
  /** 店舗の価格表示が税込か */
  isTaxIncluded: boolean;
  /** 売上計上の税基準 */
  attributionTaxBase: 'taxIncluded' | 'taxExcluded';

  /**
   * 延長料の基準時刻。会計を確定した時刻を必ず渡すこと。
   * 省略すると壁時計が進むぶんだけ客の請求が増える。
   */
  checkoutAt: Date;

  /** 在席時間で按分するときの配分ルール。既定は役割別の重み。 */
  attendanceWeights?: Record<AttendingCast['role'], number>;
}

export interface StaffBackLine {
  staffId: string;
  staffName: string;
  /** 売上（税基準変換後） */
  sales: number;
  /** 分配対象額 = (売価 - 原価) × (1 - 利益控除率) を税基準変換したもの */
  base: number;
  /** バック額 = base × バック率 */
  back: number;
  /** 内訳（画面表示・監査用） */
  breakdown: {
    itemBase: number;
    nominationBase: number;
    planBase: number;
    transferBase: number;
    /** 手当バック（同伴・ボトルバック等）。base には積まず内訳として持つ */
    allowanceBack: number;
  };
}

export interface BackCalculationResult {
  /** スタッフID別（'__shop__' は含まない） */
  lines: Map<string, StaffBackLine>;
  /** 店の取り分 */
  shopBase: number;
  /** 会計全体の総額（税基準変換前） */
  grossTotal: number;
  /** 会計全体の分配対象額（税基準変換後） */
  netSubtotal: number;
  totalCostDeduction: number;
  /** 分配先が決まらなかった額。0 でなければ運用ミスとして画面に出す */
  unassignedBase: number;
  /** 有効になった延長料（checkoutAt 基準で確定した額） */
  effectiveExtensionTotal: number;
  /** 手当の合計（客への請求額） */
  allowanceChargeTotal: number;
  /** 手当バックの合計 */
  allowanceBackTotal: number;
}

export const DEFAULT_ATTENDANCE_WEIGHTS: Record<AttendingCast['role'], number> = {
  primary: 3,
  banai: 2,
  help: 1,
  back: 1,
};

/** 税基準の変換。isTaxIncluded は「店の価格表示が税込か」 */
export function toTaxBase(
  gross: number,
  taxRate: number,
  isTaxIncluded: boolean,
  attributionTaxBase: 'taxIncluded' | 'taxExcluded'
): number {
  if (attributionTaxBase === 'taxIncluded') {
    return isTaxIncluded ? gross : gross * (1 + taxRate);
  }
  return isTaxIncluded ? gross / (1 + taxRate) : gross;
}

function ensureLine(map: Map<string, StaffBackLine>, staffId: string, staffName: string): StaffBackLine {
  let line = map.get(staffId);
  if (!line) {
    line = {
      staffId,
      staffName,
      sales: 0,
      base: 0,
      back: 0,
      breakdown: { itemBase: 0, nominationBase: 0, planBase: 0, transferBase: 0, allowanceBack: 0 },
    };
    map.set(staffId, line);
  } else if (!line.staffName && staffName) {
    line.staffName = staffName;
  }
  return line;
}

/**
 * 卓に付いたキャストへの配分比率。
 * 在席時間 × 役割の重み で按分する。releasedAt が無いキャストは会計時刻まで在席とみなす。
 */
export function resolveAttendanceShares(
  casts: AttendingCast[],
  checkoutAt: Date,
  weights: Record<AttendingCast['role'], number> = DEFAULT_ATTENDANCE_WEIGHTS
): { staffId: string; staffName: string; share: number }[] {
  if (casts.length === 0) return [];
  const scored = casts.map((c) => {
    const end = c.releasedAt ?? checkoutAt;
    const minutes = Math.max(1, Math.round((end.getTime() - c.assignedAt.getTime()) / 60000));
    return { staffId: c.staffId, staffName: c.staffName, score: minutes * (weights[c.role] ?? 1) };
  });
  const total = scored.reduce((s, x) => s + x.score, 0);
  if (total <= 0) {
    const even = 1 / scored.length;
    return scored.map((s) => ({ staffId: s.staffId, staffName: s.staffName, share: even }));
  }
  return scored.map((s) => ({ staffId: s.staffId, staffName: s.staffName, share: s.score / total }));
}

function resolveBackRate(
  staffId: string,
  item: OrderItemForDistribution | null,
  input: BackCalculationInput
): number {
  const override = input.staffBackRates?.get(staffId);
  if (override != null) return override;
  const itemRate = item?.costInfo?.baseBackRate;
  if (itemRate != null) return itemRate;
  return input.defaultBackRate;
}

/**
 * 会計1件のバックを計算する。表示も保存も承認もこの結果だけを使うこと。
 */
export function calculateBack(input: BackCalculationInput): BackCalculationResult {
  const {
    items,
    planOrders = [],
    attendingCasts = [],
    selectedSalesStaff = [],
    nominations = [],
    categoryMap,
    staffMap,
    taxRate,
    isTaxIncluded,
    attributionTaxBase,
    checkoutAt,
  } = input;

  const base = (gross: number) => toTaxBase(gross, taxRate, isTaxIncluded, attributionTaxBase);
  const lines = new Map<string, StaffBackLine>();

  let grossTotal = 0;
  let totalCostDeduction = 0;
  let shopBase = 0;
  let unassignedGross = 0;

  // 卓に付いたキャストの配分比率（商品にキャスト指定が無いときの受け皿）
  const attendanceShares = resolveAttendanceShares(
    attendingCasts,
    checkoutAt,
    input.attendanceWeights
  );
  // 会計画面で明示指定があればそちらを優先（'__shop__' は店の取り分）
  const explicitShares = selectedSalesStaff.filter((s) => s.staffId !== '__shop__');
  const shopShare = selectedSalesStaff
    .filter((s) => s.staffId === '__shop__')
    .reduce((sum, s) => sum + s.shareRate, 0);

  const fallbackShares = explicitShares.length > 0
    ? explicitShares.map((s) => ({ staffId: s.staffId, staffName: s.staffName, share: s.shareRate }))
    : attendanceShares;

  // ---- 商品 ----
  for (const item of items) {
    if (item.itemStatus === 'cancelled') continue;
    const qty = item.quantity ?? 1;
    const gross = (item.price ?? 0) * qty;
    if (gross <= 0) continue;
    grossTotal += gross;

    const cfg = item.categoryId ? categoryMap.get(item.categoryId) : undefined;
    const deductCost = cfg?.deductCost ?? false;
    const catRate = cfg?.profitDeductionRate ?? 0;
    const itemCost = deductCost ? (item.costInfo?.cost ?? 0) * qty : 0;
    if (deductCost) totalCostDeduction += itemCost;

    // 分配先の決定（優先順: 振替 → 商品単位の指定 → 会計の指定 → 卓に付いたキャスト）
    let targets: { staffId: string; staffName: string; share: number }[];
    let kind: keyof StaffBackLine['breakdown'] = 'itemBase';

    if (item.transferToStaffId) {
      targets = [{
        staffId: item.transferToStaffId,
        staffName: item.transferToStaffName ?? '',
        share: 1,
      }];
      kind = 'transferBase';
    } else if (item.salesStaff && item.salesStaff.length > 0) {
      targets = item.salesStaff.map((s) => ({
        staffId: s.staffId,
        staffName: s.staffName ?? '',
        share: s.shareRate,
      }));
    } else {
      targets = fallbackShares;
    }

    const assigned = targets.reduce((sum, t) => sum + t.share, 0);
    // 誰にも割り当たらなかったぶんは店へ寄せず、未割当として可視化する
    const leftover = Math.max(0, 1 - assigned - shopShare);

    for (const t of targets) {
      if (t.share <= 0) continue;
      const portionGross = gross * t.share;
      const portionCost = itemCost * t.share;
      const staffCfg = staffMap.get(t.staffId);
      const effRate = staffCfg?.salesProfitDeductionRate ?? catRate;
      const portionBase = base(Math.max(0, (portionGross - portionCost) * (1 - effRate)));

      const line = ensureLine(lines, t.staffId, t.staffName);
      line.sales += base(portionGross);
      line.base += portionBase;
      line.breakdown[kind] += portionBase;
      line.back += portionBase * resolveBackRate(t.staffId, item, input);
    }

    if (shopShare > 0) {
      shopBase += base(Math.max(0, (gross * shopShare - itemCost * shopShare) * (1 - catRate)));
    }
    if (leftover > 0.0001) {
      unassignedGross += gross * leftover;
    }
  }

  // ---- プラン（飲み放題・セット）と延長 ----
  let effectiveExtensionTotal = 0;
  let planGross = 0;
  for (const plan of planOrders) {
    const ext = computeEffectiveExtensionPrice(plan, checkoutAt);
    effectiveExtensionTotal += ext;
    planGross += ((plan as any).planPrice ?? 0) + ext;
  }
  grossTotal += planGross;

  if (planGross > 0) {
    const planTargets = fallbackShares;
    const assigned = planTargets.reduce((s, t) => s + t.share, 0);
    for (const t of planTargets) {
      if (t.share <= 0) continue;
      const b = base(planGross * t.share);
      const line = ensureLine(lines, t.staffId, t.staffName);
      line.sales += b;
      line.base += b;
      line.breakdown.planBase += b;
      line.back += b * resolveBackRate(t.staffId, null, input);
    }
    if (shopShare > 0) shopBase += base(planGross * shopShare);
    const leftover = Math.max(0, 1 - assigned - shopShare);
    if (leftover > 0.0001) unassignedGross += planGross * leftover;
  }

  // ---- 指名料は指名されたキャストへ ----
  // 会計画面で選ばれた売上スタッフではなく、指名された本人に付ける。
  for (const nom of nominations) {
    if (!nom.fee || nom.fee <= 0) continue;
    grossTotal += nom.fee;
    if (!nom.staffId || nom.type === 'none') {
      shopBase += base(nom.fee);
      continue;
    }
    const b = base(nom.fee);
    const line = ensureLine(lines, nom.staffId, nom.staffName ?? '');
    line.sales += b;
    line.base += b;
    line.breakdown.nominationBase += b;
    line.back += b * resolveBackRate(nom.staffId, null, input);
  }

  // ---- 手当（同伴・指名・ボトルバック等）----
  // 手当のバックは「分配対象額 × バック率」ではなく、手当そのものに
  // バック率が付いている。base には積まず back に直接足す。
  const allowanceSummary = summarizeAllowances(input.allowances ?? []);
  if (allowanceSummary.salesTotal > 0) {
    grossTotal += allowanceSummary.salesTotal;
  }
  for (const [staffId, info] of Array.from(allowanceSummary.backByStaff.entries())) {
    const line = ensureLine(lines, staffId, info.staffName);
    line.back += info.amount;
    line.breakdown.allowanceBack += info.amount;
  }
  // 担当が付いていない手当のバックは店に残る
  if (allowanceSummary.unassignedBack > 0) {
    shopBase += allowanceSummary.unassignedBack;
  }

  for (const line of Array.from(lines.values())) {
    line.sales = Math.round(line.sales);
    line.base = Math.round(line.base);
    line.back = Math.round(line.back);
    line.breakdown.itemBase = Math.round(line.breakdown.itemBase);
    line.breakdown.nominationBase = Math.round(line.breakdown.nominationBase);
    line.breakdown.planBase = Math.round(line.breakdown.planBase);
    line.breakdown.transferBase = Math.round(line.breakdown.transferBase);
    line.breakdown.allowanceBack = Math.round(line.breakdown.allowanceBack);
  }

  return {
    lines,
    shopBase: Math.round(shopBase),
    grossTotal: Math.round(grossTotal),
    netSubtotal: Math.round(base(grossTotal)),
    totalCostDeduction: Math.round(totalCostDeduction),
    unassignedBase: Math.round(base(unassignedGross)),
    effectiveExtensionTotal: Math.round(effectiveExtensionTotal),
    allowanceChargeTotal: Math.round(allowanceSummary.chargeTotal),
    allowanceBackTotal: Math.round(allowanceSummary.backTotal),
  };
}

/**
 * 会計画面の売上配分の入力を検証する。
 *
 * 実データで見つかった壊れ方:
 *   allocations: [{type:'percent', value:1050}, {type:'percent', value:3050}]
 * パーセント欄に金額が入力されていた。合計 4100% だが、実装が
 * 「各人の値 ÷ 合計」で正規化するため、エラーにならず 25.6% / 74.4% として
 * 通ってしまい、意図と違う金額が確定していた。
 *
 * 正規化そのものは残す（合計100にこだわらず比率で入れたい店もある）が、
 * 100 から離れているときは必ず画面に出す。
 */
export interface AllocationInput {
  staffId: string;
  staffName: string;
  allocationType: 'percent' | 'amount';
  allocValue: number;
}

export interface AllocationValidation {
  ok: boolean;
  errors: string[];
  warnings: string[];
  /** パーセント指定の合計 */
  percentTotal: number;
  /** 金額指定の合計 */
  amountTotal: number;
  /** 実際に各人へ渡る額 */
  resolved: { staffId: string; staffName: string; amount: number; sharePercent: number }[];
}

export function validateAllocations(
  inputs: AllocationInput[],
  netSales: number
): AllocationValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  const amountTotal = inputs
    .filter((i) => i.allocationType === 'amount')
    .reduce((s, i) => s + (i.allocValue || 0), 0);
  const percentTotal = inputs
    .filter((i) => i.allocationType === 'percent')
    .reduce((s, i) => s + (i.allocValue || 0), 0);

  if (inputs.some((i) => i.allocValue < 0)) {
    errors.push('マイナスの配分は指定できません。');
  }
  if (amountTotal > netSales) {
    errors.push(
      `金額指定の合計 ¥${amountTotal.toLocaleString()} が、分配対象 ¥${netSales.toLocaleString()} を超えています。`
    );
  }

  const hasPercent = inputs.some((i) => i.allocationType === 'percent');
  if (hasPercent) {
    if (percentTotal <= 0) {
      errors.push('パーセント指定の合計が 0 です。');
    } else if (Math.abs(percentTotal - 100) > 0.5) {
      warnings.push(
        `パーセントの合計が ${percentTotal.toLocaleString()}% です（100% ではありません）。` +
        `比率として扱い、下の「実際に渡る額」のとおりに分配します。` +
        (percentTotal > 200 ? ' 金額を入力していないか確認してください。' : '')
      );
    }
  }

  // 実際に渡る額を出す（会計側の resolveAllocations と同じ順序で計算する）
  const remainingForPercent = Math.max(0, netSales - amountTotal);
  const resolved = inputs.map((i) => {
    const amount = i.allocationType === 'amount'
      ? Math.min(i.allocValue, netSales)
      : (percentTotal > 0 ? Math.floor(remainingForPercent * (i.allocValue / percentTotal)) : 0);
    return {
      staffId: i.staffId,
      staffName: i.staffName,
      amount,
      sharePercent: netSales > 0 ? Math.round((amount / netSales) * 1000) / 10 : 0,
    };
  });

  const allocatedTotal = resolved.reduce((s, r) => s + r.amount, 0);
  const remainder = netSales - allocatedTotal;
  if (Math.abs(remainder) > 0 && !inputs.some((i) => i.staffId === '__shop__')) {
    warnings.push(
      `端数 ¥${remainder.toLocaleString()} が「店」の取り分になります。`
    );
  }

  return { ok: errors.length === 0, errors, warnings, percentTotal, amountTotal, resolved };
}
